import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

function VolumeDetail() {
  const { id } = useParams();
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [nextVolumeId, setNextVolumeId] = useState(null);
  const [issues, setIssues] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchPublications() {
      setLoading(true);
      const { data, error } = await supabase
        .from('publications')
        .select('id, title, authors, pdf_url, issue_number')
        .eq('volume_id', id)
        .order('created_at', { ascending: false });
      if (!error) setPublications(data);
      setLoading(false);
    }
    fetchPublications();
  }, [id]);

  // Fetch all unique issues in this volume
  useEffect(() => {
    async function fetchIssues() {
      const { data, error } = await supabase
        .from('publications')
        .select('issue_number')
        .eq('volume_id', id);
      if (data) {
        const uniqueIssues = Array.from(new Set(data.map(d => d.issue_number))).sort((a, b) => a - b);
        setIssues(uniqueIssues);
      }
    }
    fetchIssues();
  }, [id]);

  // Fetch next volume id
  useEffect(() => {
    async function fetchNextVolume() {
      // Get current volume_number
      const { data: currentVol, error: volErr } = await supabase
        .from('volumes')
        .select('volume_number')
        .eq('id', id)
        .single();
      if (!currentVol) return;
      // Get next volume
      const { data: nextVol, error: nextVolErr } = await supabase
        .from('volumes')
        .select('id, volume_number')
        .gt('volume_number', currentVol.volume_number)
        .order('volume_number', { ascending: true })
        .limit(1);
      if (nextVol && nextVol.length > 0) setNextVolumeId(nextVol[0].id);
      else setNextVolumeId(null);
    }
    fetchNextVolume();
  }, [id]);

  const handleSelect = (pubId) => {
    setSelected(prev => prev.includes(pubId) ? prev.filter(id => id !== pubId) : [...prev, pubId]);
  };

  const handleSelectAll = () => {
    if (selected.length === publications.length) {
      setSelected([]);
    } else {
      setSelected(publications.map(pub => pub.id));
    }
  };

  const handleDownload = async (all = false) => {
    const pubsToDownload = publications.filter(pub => (all ? true : selected.includes(pub.id)) && pub.pdf_url);
    if (pubsToDownload.length === 0) return;
    if (pubsToDownload.length === 1) {
      window.open(`https://rbvgtaqimzpsarvoxubn.supabase.co/storage/v1/object/public/pdf/${pubsToDownload[0].pdf_url.split('/').pop()}`, '_blank');
      return;
    }
    const zip = new JSZip();
    const projectRef = 'rbvgtaqimzpsarvoxubn';
    const bucket = 'pdf';
    const baseUrl = `https://${projectRef}.supabase.co/storage/v1/object/public/${bucket}/`;
    await Promise.all(pubsToDownload.map(async pub => {
      const fileName = pub.pdf_url.split('/').pop();
      try {
        const response = await fetch(baseUrl + fileName);
        if (response.ok) {
          const blob = await response.blob();
          zip.file(fileName, blob);
        }
      } catch (e) {}
    }));
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, all ? 'full-volume.zip' : 'publications.zip');
  };

  return (
    <div className="volume-detail-page container-fluid px-0">
      <div className="row gx-0">
        <div className="col-md-3 d-none d-md-block">
          <div className="bg-white border-end vh-100 position-sticky" style={{top: '72px', zIndex: 1, minHeight: 'calc(100vh - 72px - 56px)'}}>
            <div className="p-4">
              <h5 className="mb-3">Actions</h5>
              <button type="button" className="btn btn-outline-primary w-100 mb-2" onClick={handleSelectAll} disabled={publications.length === 0}>
                {selected.length === publications.length ? 'Unselect All' : 'Select All Publications'}
              </button>
              <button type="button" className="btn btn-success w-100" onClick={() => { setSelected(publications.map(pub => pub.id)); handleDownload(true); }} disabled={publications.length === 0}>
                Download Full Volume
              </button>
            </div>
          </div>
        </div>
        <div className="col-md-9 col-12" style={{minHeight: 'calc(100vh - 72px - 56px)'}}>
          <div className="py-4 px-3 px-md-5">
            <div className="d-flex justify-content-end mb-3 gap-2">
              {nextVolumeId && (
                <button className="btn btn-outline-success" onClick={() => navigate(`/volume/${nextVolumeId}`)}>Next Volume</button>
              )}
            </div>
            <h2 className="mb-4 text-center">Publications in this Volume</h2>
            {loading ? (
              <div className="text-center my-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : publications.length === 0 ? (
              <div className="alert alert-info">No publications found for this volume.</div>
            ) : (
              <form>
                <ul className="list-group mb-3">
                  {publications.map(pub => (
                    <li key={pub.id} className="list-group-item d-flex align-items-center py-4" style={{fontSize: '1.15rem'}}>
                      <input type="checkbox" className="form-check-input me-3" checked={selected.includes(pub.id)} onChange={() => handleSelect(pub.id)} style={{width: '1.3em', height: '1.3em'}} />
                      <span className="flex-grow-1">
                        <Link to={`/publication/${pub.id}`}>{pub.title}</Link> <span className="text-muted">({pub.authors})</span>
                      </span>
                      {pub.pdf_url && (
                        <a
                          href={`https://rbvgtaqimzpsarvoxubn.supabase.co/storage/v1/object/public/pdf/${pub.pdf_url.split('/').pop()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-secondary ms-2"
                        >
                          PDF
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
                {selected.length > 0 && (
                  <button type="button" className="btn btn-success" onClick={() => handleDownload(false)}>Download Selected Publications</button>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default VolumeDetail; 